import React, {
  FC,
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { Button } from "@tarojs/components";
import { ButtonProps } from "@tarojs/components/types/Button";
import {
  ITouchEvent,
  TouchEventFunction,
} from "@tarojs/components/types/common";

import { useTaroEnv } from "../../hooks";
import "./index.scss";
import DotJump from "./DotJump";

export type ButtonType = "default" | "secondary" | "primary" | "warn";

export type ButtonShape = "square" | "round" | "rectangle";

export type ButtonSize = "default" | "mini" | "full";

export type XhButtonProps = {
  htmlType?: "button" | "submit" | "reset";
  type?: ButtonType;
  size?: ButtonSize;
  shape?: ButtonShape;
  disabled?: boolean;
  className?: string;
  styleName?: string;
  text?: string;
  preventTime?: number;
  onClick?: (e: ITouchEvent | MouseEvent) => void;
  style?: React.CSSProperties;
  loading?: boolean;
  loadingStyle?: React.CSSProperties;
  loadingSpace?: number;
  ghost?: boolean;
  /**
   * 镂空
   */
  hollow?: boolean;
  dataTrackId?: string;
} & Omit<ButtonProps, "type" | "size">;

const XhButton: FC<XhButtonProps> = props => {
  const {
    disabled = false,
    type = "default",
    size = "normal",
    shape = "round",
    children,
    className,
    htmlType,
    formType,
    text,
    openType,
    lang,
    sessionFrom,
    sendMessageTitle,
    sendMessagePath,
    sendMessageImg,
    showMessageCard,
    appParameter,
    onGetUserInfo,
    onGetPhoneNumber,
    onOpenSetting,
    onError,
    onContact,
    onClick,
    preventTime = 2500,
    style,
    loading,
    loadingStyle,
    loadingSpace,
    ghost = false,
    hollow = false,
    dataTrackId = "",
  } = props;
  const state = useTaroEnv();
  const [preventClick, setPreventClick] = useState(false);
  const timer = useRef<NodeJS.Timeout | undefined>();
  const classObj = classNames(
    "xh-button",
    {
      default: type === "default",
      primary: type === "primary",
      secondary: type === "secondary",
      ghost: ghost,
      hollow: hollow,
      warn: type === "warn",
      quare: shape === "square",
      round: shape === "round",
      rectangle: shape === "rectangle",
      normal: size === "normal",
      small: size === "mini",
      full: size === "full",
      disabled: disabled,
      webutton: state === "WEAPP",
    },
    className
  );

  const handleClick: MouseEventHandler & TouchEventFunction = e => {
    state === "WEB" && e.nativeEvent.stopImmediatePropagation();
    e.preventDefault();

    if (preventClick && preventTime !== 0) {
      console.log("重复点击");
      return;
    }
    if (preventTime !== 0) {
      timer.current = setTimeout(() => {
        setPreventClick(false);
        clearTimeout(timer.current);
      }, preventTime);
    }
    if (preventTime !== 0) setPreventClick(true);
    console.log("正常点击");
    onClick && onClick(e);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timer.current);
    };
  }, []);

  const weButton = (
    <Button
      // @ts-ignore
      dataTrackId={dataTrackId}
      className={classObj}
      formType={formType}
      openType={openType}
      lang={lang}
      type={type as ButtonProps["type"]}
      sessionFrom={sessionFrom}
      sendMessageTitle={sendMessageTitle}
      sendMessagePath={sendMessagePath}
      sendMessageImg={sendMessageImg}
      showMessageCard={showMessageCard}
      appParameter={appParameter}
      onGetUserInfo={onGetUserInfo}
      onGetPhoneNumber={onGetPhoneNumber}
      onOpenSetting={onOpenSetting}
      onError={onError}
      onContact={onContact}
      onClick={handleClick}
      disabled={disabled}
      style={style}
      loading={loading}
    >
      {children}
    </Button>
  );

  if (state !== "WEB") {
    return weButton;
  }

  return (
    // eslint-disable-next-line react/button-has-type
    <button
      data-track-id={dataTrackId}
      className={classObj}
      disabled={disabled}
      type={htmlType}
      onClick={disabled ? undefined : handleClick}
      style={style}
    >
      {children || text}
      {loading && <DotJump style={loadingStyle} space={loadingSpace} />}
    </button>
  );
};

export default XhButton;
